/*
  Spellbound product (sections/spellbound-product.liquid).

  One mount per [data-spellbound-product]:
    - gallery: slides ([data-sb-slide]) + thumbnails ([data-sb-thumb]),
      mobile snap rail with swipe + arrow keys, <dialog> lightbox,
      #fade_to_image crossfade enabled once the glow layers have decoded
    - variant picker: radio pills resolve a variant from the JSON in
      [data-sb-variants]; updates price, button state, hidden name="id",
      ?variant= in the URL, option availability and the active media
    - quantity stepper
  Add to cart itself is the theme's <product-form> (assets/product-form.js).

  Theme editor safe: shopify:section:load / unload mount and tear down.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-spellbound-product]';
  var DESKTOP = '(min-width: 760px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';
  var mounts = new Map();

  function parseJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function mount(root) {
    if (!root || mounts.has(root)) return;
    var cleanups = [];
    var on = function (target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(function () {
        target.removeEventListener(type, handler, options);
      });
    };

    var desktop = window.matchMedia(DESKTOP);
    var reduced = window.matchMedia(REDUCED);

    /* ---------------- Gallery ---------------- */

    var rail = root.querySelector('[data-sb-rail]');
    var slides = rail ? Array.prototype.slice.call(rail.querySelectorAll('[data-sb-slide]')) : [];
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-sb-thumb]'));
    var dialog = root.querySelector('[data-sb-dialog]');
    var activeIndex = Math.max(
      0,
      slides.findIndex(function (slide) {
        return slide.classList.contains('is-active');
      }),
    );
    var scrollTicking = false;

    function slideOffset(index) {
      return slides[index].offsetLeft - slides[0].offsetLeft;
    }

    function pauseInactiveMedia() {
      slides.forEach(function (slide, index) {
        if (index === activeIndex) return;
        slide.querySelectorAll('video').forEach(function (video) {
          if (!video.paused) video.pause();
        });
        var deferred = slide.querySelector('deferred-media');
        if (deferred && typeof deferred.pause === 'function') deferred.pause();
      });
    }

    function renderGallery() {
      slides.forEach(function (slide, index) {
        var active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('button, a, [tabindex]').forEach(function (el) {
          if (el.hasAttribute('data-sb-keep-tab')) return;
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
      thumbs.forEach(function (thumb) {
        var slide = slides[activeIndex];
        thumb.setAttribute(
          'aria-pressed',
          slide && thumb.dataset.mediaId === slide.dataset.mediaId ? 'true' : 'false',
        );
      });
      pauseInactiveMedia();
    }

    function moveGallery(index, smooth) {
      if (!slides.length) return;
      index = Math.max(0, Math.min(slides.length - 1, index));
      activeIndex = index;
      renderGallery();
      if (!desktop.matches && rail) {
        var behavior = smooth !== false && !reduced.matches ? 'smooth' : 'auto';
        rail.scrollTo({ left: slideOffset(index), behavior: behavior });
      }
    }

    function selectMediaById(mediaId) {
      if (mediaId === undefined || mediaId === null) return false;
      var index = slides.findIndex(function (slide) {
        return slide.dataset.mediaId === String(mediaId);
      });
      if (index < 0) return false;
      moveGallery(index, true);
      return true;
    }

    thumbs.forEach(function (thumb) {
      on(thumb, 'click', function () {
        selectMediaById(thumb.dataset.mediaId);
      });
    });

    if (rail) {
      on(
        rail,
        'scroll',
        function () {
          if (desktop.matches || scrollTicking) return;
          scrollTicking = true;
          window.requestAnimationFrame(function () {
            scrollTicking = false;
            var nearest = 0;
            var best = Infinity;
            slides.forEach(function (slide, index) {
              var distance = Math.abs(slideOffset(index) - rail.scrollLeft);
              if (distance < best) {
                best = distance;
                nearest = index;
              }
            });
            if (nearest !== activeIndex) {
              activeIndex = nearest;
              renderGallery();
            }
          });
        },
        { passive: true },
      );

      on(rail, 'keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        moveGallery(activeIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
      });

      var realign = function () {
        if (!desktop.matches) rail.scrollTo({ left: slideOffset(activeIndex), behavior: 'auto' });
      };
      var resizeObserver = null;
      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(realign);
        resizeObserver.observe(rail);
        cleanups.push(function () {
          resizeObserver.disconnect();
        });
      } else {
        on(window, 'resize', realign);
      }
      on(desktop, 'change', realign);
    }

    /* Lightbox */

    var dialogImage = dialog ? dialog.querySelector('[data-sb-dialog-image]') : null;
    var dialogFade = dialog ? dialog.querySelector('[data-sb-dialog-fade]') : null;
    var dialogMedia = dialog ? dialog.querySelector('[data-sb-dialog-media]') : null;
    var dialogTitle = dialog ? dialog.querySelector('[data-sb-dialog-title]') : null;
    var productTitle = root.dataset.productTitle || '';

    function openDialog(slide) {
      if (!dialog || typeof dialog.showModal !== 'function') return;
      var trigger = slide.querySelector('[data-sb-enlarge]');
      var image = slide.querySelector('.spellbound-product__image');
      var fadeLayer = slide.querySelector('[data-sb-fade-layer]');
      if (!trigger || !image || !dialogImage) return;

      dialogImage.src = trigger.dataset.sbFull || image.currentSrc || image.src;
      dialogImage.alt = image.alt || '';
      if (dialogFade) {
        if (fadeLayer) {
          dialogFade.src = fadeLayer.dataset.sbFadeFull || fadeLayer.currentSrc || fadeLayer.src;
          dialogFade.hidden = false;
          var maskUrl = fadeLayer.dataset.sbFadeMask || '';
          if (maskUrl && maskUrl.indexOf('(') === -1 && maskUrl.indexOf(')') === -1 && maskUrl.indexOf("'") === -1 && maskUrl.indexOf('"') === -1) {
            dialogFade.style.setProperty('--sb-fade-mask', "url('" + maskUrl + "')");
          } else {
            dialogFade.style.removeProperty('--sb-fade-mask');
          }
        } else {
          dialogFade.hidden = true;
          dialogFade.removeAttribute('src');
          dialogFade.style.removeProperty('--sb-fade-mask');
        }
      }
      if (dialogMedia) dialogMedia.classList.toggle('show-glow', !!fadeLayer && !reduced.matches);
      if (dialogTitle) {
        var label = slide.dataset.slideTitle || '';
        dialogTitle.textContent = label && label !== productTitle ? productTitle + ' · ' + label : productTitle;
      }
      dialog.showModal();
    }

    slides.forEach(function (slide) {
      var trigger = slide.querySelector('[data-sb-enlarge]');
      if (!trigger) return;
      on(trigger, 'click', function (event) {
        event.preventDefault();
        var index = slides.indexOf(slide);
        if (index >= 0 && index !== activeIndex) moveGallery(index, false);
        openDialog(slide);
      });
    });

    if (dialog) {
      on(dialog.querySelector('[data-sb-dialog-close]'), 'click', function () {
        dialog.close();
      });
      on(dialog, 'click', function (event) {
        if (event.target !== dialog) return;
        var box = dialog.getBoundingClientRect();
        if (
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom
        ) {
          dialog.close();
        }
      });
      on(dialog, 'close', function () {
        if (dialogFade) {
          dialogFade.hidden = true;
          dialogFade.removeAttribute('src');
          dialogFade.style.removeProperty('--sb-fade-mask');
        }
        if (dialogMedia) dialogMedia.classList.remove('show-glow');
      });
    }

    /* Crossfade CSS runs on .is-active without waiting for decode. Mark the
       root ready once layers have decoded (or after a short fallback) so a
       hung decode() cannot leave the overlay stuck at opacity 0. */
    var fadeLayers = Array.prototype.slice.call(root.querySelectorAll('[data-sb-fade-layer]'));
    if (fadeLayers.length) {
      var markReady = function () {
        if (mounts.has(root)) root.classList.add('is-motion-ready');
      };
      var ready = fadeLayers.map(function (img) {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        if (typeof img.decode === 'function') {
          return img.decode().catch(function () {});
        }
        return new Promise(function (resolve) {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      });
      Promise.all(ready).then(markReady);
      window.setTimeout(markReady, 800);
    }

    renderGallery();
    if (rail && !desktop.matches && activeIndex > 0) {
      rail.scrollTo({ left: slideOffset(activeIndex), behavior: 'auto' });
    }

    /* ---------------- Quantity ---------------- */

    var quantityRoot = root.querySelector('[data-sb-quantity]');
    if (quantityRoot) {
      var qtyInput = quantityRoot.querySelector('[data-sb-quantity-input]');
      var qtyMinus = quantityRoot.querySelector('[data-sb-quantity-minus]');
      var qtyPlus = quantityRoot.querySelector('[data-sb-quantity-plus]');
      var min = parseInt(qtyInput.getAttribute('min'), 10) || 1;
      var step = parseInt(qtyInput.getAttribute('step'), 10) || 1;

      var readQty = function () {
        var value = parseInt(qtyInput.value, 10);
        return isNaN(value) ? min : value;
      };
      var writeQty = function (value) {
        var max = parseInt(qtyInput.getAttribute('max'), 10);
        value = Math.max(min, value);
        if (!isNaN(max)) value = Math.min(max, value);
        qtyInput.value = String(value);
        if (qtyMinus) qtyMinus.disabled = value <= min;
        if (qtyPlus) qtyPlus.disabled = !isNaN(max) && value >= max;
      };

      on(qtyMinus, 'click', function () {
        writeQty(readQty() - step);
      });
      on(qtyPlus, 'click', function () {
        writeQty(readQty() + step);
      });
      on(qtyInput, 'change', function () {
        writeQty(readQty());
      });
      writeQty(readQty());
    }

    /* ---------------- Variants ---------------- */

    var picker = root.querySelector('[data-sb-variant-picker]');
    var variants = picker ? parseJSON(picker.querySelector('[data-sb-variants]')) : null;
    var priceBox = root.querySelector('[data-sb-price]');
    var buyBox = root.querySelector('[data-sb-buy]');
    var addButton = root.querySelector('[data-sb-add]');
    var addLabel = root.querySelector('[data-sb-add-label]');
    var variantInput = root.querySelector('[data-sb-variant-id]');
    var labels = {
      add: (buyBox && buyBox.dataset.addLabel) || root.dataset.addLabel || 'Add to cart',
      soldOut: (buyBox && buyBox.dataset.soldOutLabel) || root.dataset.soldOutLabel || 'Sold out',
      unavailable: root.dataset.unavailableLabel || 'Unavailable',
    };

    function selectedOptions() {
      var fieldsets = Array.prototype.slice.call(picker.querySelectorAll('[data-sb-option-index]'));
      return fieldsets.map(function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function findVariant(options) {
      return variants.find(function (variant) {
        return options.every(function (value, index) {
          return value === null || variant.options[index] === value;
        });
      });
    }

    function updatePrice(variant) {
      if (!priceBox) return;
      var template = root.querySelector('template[data-sb-price-for="' + variant.id + '"]');
      if (template) priceBox.innerHTML = template.innerHTML;
    }

    function updateButton(variant) {
      if (!addButton) return;
      if (!variant) {
        addButton.disabled = true;
        if (addLabel) addLabel.textContent = labels.unavailable;
        return;
      }
      addButton.disabled = !variant.available;
      addButton.removeAttribute('aria-disabled');
      if (addLabel) addLabel.textContent = variant.available ? labels.add : labels.soldOut;
    }

    function updateAvailability(options) {
      var fieldsets = Array.prototype.slice.call(picker.querySelectorAll('[data-sb-option-index]'));
      fieldsets.forEach(function (fieldset, optionIndex) {
        fieldset.querySelectorAll('[data-sb-option-value]').forEach(function (label) {
          var input = label.querySelector('input');
          var candidate = options.slice();
          candidate[optionIndex] = input.value;
          var available = variants.some(function (variant) {
            return (
              variant.available &&
              candidate.every(function (value, index) {
                return value === null || variant.options[index] === value;
              })
            );
          });
          label.classList.toggle('is-unavailable', !available);
        });
        var selected = fieldset.querySelector('[data-sb-selected-label]');
        if (selected) {
          var checked = fieldset.querySelector('input:checked');
          selected.textContent = checked ? '· ' + checked.value : '';
        }
      });
    }

    function updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      if (window.Shopify && window.Shopify.designMode) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    function onVariantChange() {
      var options = selectedOptions();
      var variant = findVariant(options);
      updateAvailability(options);
      updateButton(variant);
      if (!variant) return;
      updatePrice(variant);
      if (variantInput) variantInput.value = variant.id;
      updateUrl(variant);
      if (variant.featured_media && variant.featured_media.id) {
        selectMediaById(variant.featured_media.id);
      }
      root.dispatchEvent(
        new CustomEvent('spellbound-product:variant', { bubbles: true, detail: { variant: variant } }),
      );
    }

    if (picker && Array.isArray(variants) && variants.length) {
      picker.querySelectorAll('[data-sb-option-input]').forEach(function (input) {
        on(input, 'change', onVariantChange);
      });
      updateAvailability(selectedOptions());
    }

    mounts.set(root, function () {
      cleanups.forEach(function (fn) {
        fn();
      });
      mounts.delete(root);
    });
  }

  function unmount(root) {
    var destroy = mounts.get(root);
    if (destroy) destroy();
  }

  function mountAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll();
    });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach(unmount);
  });
})();
